/** SQLite record format (big-endian varints), as Turso CDC images and replication rows carry it. */
export type RecordValue = null | number | string | Uint8Array

export function decodeRecord(bytes: Uint8Array): RecordValue[] {
  let offset = 0
  const varint = () => {
    let result = 0
    for (let index = 0; index < 9; index++) {
      const byte = bytes[offset++]
      if (index === 8) return result * 256 + byte
      result = result * 128 + (byte & 127)
      if (!(byte & 128)) return result
    }
    return result
  }
  const headerSize = varint()
  const types: number[] = []
  for (let read = offset; read < headerSize; read = offset) types.push(varint())
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  return types.map(type => {
    if (type === 0) return null
    if (type === 8) return 0
    if (type === 9) return 1
    if (type >= 1 && type <= 6) {
      const size = [0, 1, 2, 3, 4, 6, 8][type]
      let value = 0n
      for (let index = 0; index < size; index++) value = (value << 8n) | BigInt(bytes[offset + index])
      if (size > 0 && bytes[offset] & 128) value -= 1n << BigInt(size * 8)
      offset += size
      return Number(value)
    }
    if (type === 7) { const value = view.getFloat64(offset); offset += 8; return value }
    const size = (type - (type % 2 === 0 ? 12 : 13)) / 2
    const slice = bytes.slice(offset, offset + size)
    offset += size
    return type % 2 === 0 ? slice : new TextDecoder().decode(slice)
  })
}

export function encodeRecord(values: RecordValue[]): Uint8Array {
  const payload: Uint8Array[] = []
  const types = values.map(value => {
    if (value === null) { payload.push(new Uint8Array()); return 0 }
    if (typeof value === 'number') {
      if (value === 0 || value === 1) { payload.push(new Uint8Array()); return 8 + value }
      const bytes = new Uint8Array(8)
      new DataView(bytes.buffer).setBigInt64(0, BigInt(value))
      payload.push(bytes)
      return 6
    }
    const bytes = typeof value === 'string' ? new TextEncoder().encode(value) : value
    payload.push(bytes)
    return bytes.length * 2 + (typeof value === 'string' ? 13 : 12)
  })
  const header = concat(types.map(sqliteVarint))
  const size = header.length + 1
  if (size > 127) throw new Error('Record header too large')
  return concat([Uint8Array.of(size), header, ...payload])
}

function sqliteVarint(value: number): Uint8Array {
  const bytes = [value & 127]
  for (let rest = Math.floor(value / 128); rest > 0; rest = Math.floor(rest / 128)) bytes.unshift((rest & 127) | 128)
  return Uint8Array.from(bytes)
}

function concat(parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((size, part) => size + part.length, 0))
  let at = 0
  for (const part of parts) { out.set(part, at); at += part.length }
  return out
}
